import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check } from 'lucide-react';
import { getProductById } from '../data/products';
import useCartStore from '../store/cartStore';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = getProductById(id);
  const [selectedSize, setSelectedSize] = useState(product ? product.sizes[0] : '500g');
  const [added, setAdded] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [promoStatus, setPromoStatus] = useState({ active: true, limit: 15, remaining: 12, sold: 3 });
  const [stockStatus, setStockStatus] = useState(null);
  const addToCart = useCartStore(state => state.addToCart);

  const isOutOfStock = stockStatus
    ? !(stockStatus[product?.name]?.in_stock ?? true)
    : (product?.outOfStock || false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stock-status`)
      .then(r => r.json())
      .then(data => setStockStatus(data))
      .catch(() => {});
    if (id === 'honey_bundle') {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundle-promo-status`)
        .then(r => r.json())
        .then(data => setPromoStatus(data))
        .catch(() => {});
    }
  }, [id]);

  if (!product) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-foreground mb-4">Product not found</h1>
          <button
            onClick={() => navigate('/products')}
            className="text-primary hover:underline"
          >
            Return to shop
          </button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product, selectedSize);
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="min-h-screen pb-24 pt-56">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <button
          onClick={() => navigate('/products')}
          className="inline-flex items-center space-x-2 text-foreground/70 hover:text-primary transition-colors mb-8"
          data-testid="back-to-products-button"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to products</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <div className={`relative overflow-hidden aspect-square flex items-center justify-center bg-white`} style={{ borderRadius: '1rem' }}>
              <img
                src={product.image}
                alt={product.name}
                className={`max-w-full max-h-full object-contain ${product.id === 'honey_bundle' ? 'p-4' : 'p-8'}`}
                style={{ filter: product.imageFilter, borderRadius: '1rem' }}
                data-testid="product-image"
              />
            </div>
            {product.id === 'honey_bundle' && (
              <div className="relative overflow-hidden aspect-video" style={{ borderRadius: '1rem' }}>
                <video
                  src="https://customer-assets.emergentagent.com/job_bee-harvest-6/artifacts/xqsr4snm_Luxury%20Honey%20Animation.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  style={{ borderRadius: '1rem' }}
                  data-testid="bundle-video"
                />
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col justify-center"
          >
            <p className="text-sm uppercase tracking-wide text-muted-foreground mb-2" data-testid="product-origin">
              {product.origin}
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="product-name">
              {product.name}
            </h1>
            <p className="text-xl md:text-2xl text-foreground/70 mb-8" data-testid="product-tagline">
              {product.tagline}
            </p>

            {product.id === 'honey_bundle' && promoStatus.active && (
              <div className="mb-8 p-5 rounded-2xl border-2 border-amber-400 bg-amber-50" data-testid="bundle-promo-banner">
                <p className="text-xs font-bold uppercase tracking-widest text-amber-700 mb-2">First {promoStatus.limit} Only — {promoStatus.remaining} remaining</p>
                <p className="text-base font-semibold text-foreground leading-snug">
                  Order a bundle from the collection and receive a <span className="text-amber-600">FREE gold tasting spoon + honey dipper</span>
                </p>
                <p className="text-sm text-foreground/60 mt-1">The perfect pairing for your Ricky's Bees collection.</p>
              </div>
            )}

            <div className="space-y-6 mb-8">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Description</h3>
                <p className="text-foreground/70 leading-relaxed" data-testid="product-description">
                  {product.longDescription}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Color</h4>
                  <p className="text-sm text-foreground/70">{product.color}</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Taste</h4>
                  <p className="text-sm text-foreground/70">{product.taste}</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Uses</h4>
                  <p className="text-sm text-foreground/70">{product.uses}</p>
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-8">
              <div className="mb-6">
                <label className="block text-sm font-semibold text-foreground mb-3">Select Size</label>
                <div className="flex space-x-4">
                  {product.sizes.map(size => {
                    return (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`px-4 py-3 rounded-lg border-2 transition-all duration-300 ${
                          selectedSize === size
                            ? 'border-primary bg-primary/5 text-primary font-semibold'
                            : 'border-border hover:border-primary/50 text-foreground'
                        }`}
                        data-testid={`size-option-${size}`}
                      >
                        <div className="text-lg font-semibold">
                          {size}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <span className="text-3xl font-bold text-primary" data-testid="selected-price">
                  ${product.price[selectedSize] || Object.values(product.price)[0]}
                </span>
                <div className="flex items-center space-x-3 border border-border rounded-lg">
                  <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="px-4 py-2 hover:bg-muted transition-colors rounded-l-lg text-lg font-medium" data-testid="decrease-quantity">
                    -
                  </button>
                  <span className="px-4 font-semibold text-lg" data-testid="product-quantity">{quantity}</span>
                  <button onClick={() => setQuantity(q => q + 1)} className="px-4 py-2 hover:bg-muted transition-colors rounded-r-lg text-lg font-medium" data-testid="increase-quantity">
                    +
                  </button>
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={isOutOfStock}
                className={`w-full px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 ${
                  isOutOfStock
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : added
                    ? 'bg-green-600 text-white'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02]'
                }`}
                data-testid="add-to-cart-button"
              >
                {isOutOfStock ? (
                  'Sold Out'
                ) : added ? (
                  <span className="flex items-center justify-center space-x-2">
                    <Check className="w-5 h-5" />
                    <span>Added to Cart</span>
                  </span>
                ) : (
                  'Add to Cart'
                )}
              </button>

              <p className="text-sm text-foreground/70 mt-4" data-testid="shipping-info">
                Flat rate shipping Australia-wide
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
